type LocalAvailability =
  | "available"
  | "downloadable"
  | "downloading"
  | "unavailable";

type SpeechAvailability = "local" | "browser" | "unsupported";

type RecognitionAlternative = {
  transcript: string;
  confidence: number;
};

type RecognitionResult = {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: RecognitionAlternative;
};

type RecognitionResultList = {
  readonly length: number;
  [index: number]: RecognitionResult;
};

type RecognitionEvent = Event & {
  resultIndex: number;
  results: RecognitionResultList;
};

interface LocalSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  processLocally?: boolean;
  onaudiostart?: (() => void) | null;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: Event & { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

export const isAndroidSpeechBrowser = (): boolean =>
  typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

export const isMobileSpeechBrowser = (): boolean =>
  typeof navigator !== "undefined" &&
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

interface LocalSpeechRecognitionConstructor {
  new (): LocalSpeechRecognition;
  available?(options: {
    langs: string[];
    processLocally: boolean;
  }): Promise<LocalAvailability>;
}

declare global {
  interface Window {
    SpeechRecognition?: LocalSpeechRecognitionConstructor;
    webkitSpeechRecognition?: LocalSpeechRecognitionConstructor;
  }
}

export class LocalSpeechController {
  private recognition: LocalSpeechRecognition | undefined;
  private enabled = false;
  private recognitionRunning = false;
  private inputActive = true;
  private playbackActive = false;
  private restartTimer = 0;
  private sequence = 0;
  private readonly androidBrowser = isAndroidSpeechBrowser();
  private readonly mobileBrowser = isMobileSpeechBrowser();

  constructor(
    private readonly onCaption: (
      text: string,
      final: boolean,
      sequence: number,
    ) => void,
    private readonly onState: (state: string, enabled: boolean) => void,
  ) {}

  async check(): Promise<SpeechAvailability> {
    const Recognition =
      window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!Recognition) {
      this.onState("이 브라우저는 한국어 자막을 지원하지 않습니다", false);
      return "unsupported";
    }
    if (!Recognition.available) {
      this.onState("한국어 자막 사용 가능", false);
      return "browser";
    }
    try {
      const availability = await Recognition.available({
        langs: ["ko-KR"],
        processLocally: true,
      });
      if (availability === "available") {
        this.onState("한국어 자막 사용 가능", false);
        return "local";
      }
      this.onState("한국어 자막 사용 가능", false);
      return "browser";
    } catch {
      this.onState("한국어 자막 사용 가능", false);
      return "browser";
    }
  }

  enable(): Promise<boolean> {
    const Recognition =
      window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!Recognition) return Promise.resolve(false);

    // 모바일 브라우저는 자막 권한 요청을 위해 회의 입장 탭의 사용자 활성화가
    // 살아 있는 동안 start()를 호출해야 합니다. 지원 여부 확인을 기다리면 그
    // 활성화를 잃을 수 있으므로 모바일에서는 브라우저 기본 인식을 바로 씁니다.
    if (this.mobileBrowser || !Recognition.available) {
      return Promise.resolve(this.startRecognition(Recognition, "browser"));
    }

    return this.enableAfterAvailabilityCheck(Recognition);
  }

  private async enableAfterAvailabilityCheck(
    Recognition: LocalSpeechRecognitionConstructor,
  ): Promise<boolean> {
    const availability = await this.check();
    if (availability === "unsupported") return false;
    return this.startRecognition(Recognition, availability);
  }

  private startRecognition(
    Recognition: LocalSpeechRecognitionConstructor,
    availability: Exclude<SpeechAvailability, "unsupported">,
  ): boolean {
    const recognition = new Recognition();
    recognition.lang = "ko-KR";
    // Chrome for Android ignores continuous=true. Explicit single-utterance
    // mode plus the existing onend restart is more reliable on mobile while
    // retaining the desktop behavior unchanged.
    recognition.continuous = !this.androidBrowser;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    if (availability === "local") {
      recognition.processLocally = true;
    }
    recognition.onaudiostart = () => {
      if (this.mobileBrowser && this.enabled && !this.isSuspended()) {
        this.onState("한국어 자막 켜짐", true);
      }
    };
    recognition.onresult = (event) => {
      if (this.isSuspended()) return;
      if (this.mobileBrowser) this.onState("한국어 자막 켜짐", true);
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const text = result?.[0]?.transcript.trim() ?? "";
        if (!text) continue;
        const final = Boolean(result?.isFinal);
        this.onCaption(text, final, this.sequence);
        if (final) this.sequence += 1;
      }
    };
    recognition.onerror = (event) => {
      const error = event.error ?? "unknown";
      if (error === "aborted" || error === "no-speech") return;

      if (
        error === "not-allowed" ||
        error === "service-not-allowed" ||
        error === "audio-capture" ||
        error === "language-not-supported"
      ) {
        this.enabled = false;
        this.recognitionRunning = false;
        this.clearRestartTimer();
        this.recognition = undefined;
        const state =
          error === "not-allowed" || error === "service-not-allowed"
            ? "자막 권한이 차단되었습니다 · 브라우저 설정을 확인하세요"
            : error === "audio-capture"
              ? "자막용 마이크를 열지 못했습니다"
              : "이 브라우저는 한국어 자막을 지원하지 않습니다";
        this.onState(state, false);
        return;
      }

      this.onState(`자막 오류: ${error} · 다시 연결 중`, true);
    };
    recognition.onend = () => {
      this.recognitionRunning = false;
      if (this.enabled && !this.isSuspended()) {
        if (this.mobileBrowser) {
          this.onState("한국어 자막 다시 연결 중", true);
        }
        this.scheduleRestart(250);
      }
    };
    this.recognition = recognition;
    this.enabled = true;
    if (this.isSuspended()) {
      this.syncRecognitionActivity();
      return true;
    }
    try {
      recognition.start();
      this.recognitionRunning = true;
    } catch {
      this.enabled = false;
      this.recognition = undefined;
      this.onState("자막을 시작하지 못했습니다", false);
      return false;
    }
    this.onState(
      this.mobileBrowser ? "한국어 자막 연결 중" : "한국어 자막 켜짐",
      true,
    );
    return true;
  }

  disable(): void {
    this.enabled = false;
    this.clearRestartTimer();
    this.recognition?.stop();
    this.recognition = undefined;
    this.recognitionRunning = false;
    this.onState("한국어 자막 꺼짐", false);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setInputActive(active: boolean): void {
    if (this.inputActive === active) return;
    this.inputActive = active;
    this.syncRecognitionActivity();
  }

  setPlaybackActive(active: boolean): void {
    if (this.playbackActive === active) return;
    this.playbackActive = active;
    this.syncRecognitionActivity();
  }

  private isSuspended(): boolean {
    return !this.inputActive || this.playbackActive;
  }

  private syncRecognitionActivity(): void {
    this.clearRestartTimer();
    if (!this.enabled || !this.recognition) return;
    if (this.isSuspended()) {
      if (this.recognitionRunning) {
        try {
          this.recognition.abort();
        } catch {
          // 이미 종료 중인 인식기는 onend에서 상태를 정리합니다.
        }
      }
      this.onState(
        this.playbackActive
          ? "텍스트 음성 재생 중 · 자막 일시 중지"
          : "마이크 꺼짐 · 자막 일시 중지",
        true,
      );
      return;
    }
    this.onState("한국어 자막 켜짐", true);
    this.scheduleRestart(350);
  }

  private scheduleRestart(delay: number): void {
    this.clearRestartTimer();
    this.restartTimer = window.setTimeout(() => {
      this.restartTimer = 0;
      if (
        !this.enabled ||
        this.isSuspended() ||
        this.recognitionRunning ||
        !this.recognition
      ) {
        return;
      }
      try {
        this.recognition.start();
        this.recognitionRunning = true;
      } catch {
        this.enabled = false;
        this.recognition = undefined;
        this.onState("자막을 다시 시작하지 못했습니다", false);
      }
    }, delay);
  }

  private clearRestartTimer(): void {
    window.clearTimeout(this.restartTimer);
    this.restartTimer = 0;
  }
}
